import './barcode-subscriber.scss';

import { defineComponent } from 'vue';
import { faBarcode, faCamera, faCircleNotch, faLightbulb, faSyncAlt, faMagic } from '@fortawesome/free-solid-svg-icons';
import { first } from 'rxjs/operators';
import { QuaggaJSCodeReader, QuaggaJSReaderConfig } from '@ericblade/quagga2';

import { reactiveVueMixin } from '@skinsoft/reactive-vue';

import { input$ } from '../keyboard';
import { CameraScanner } from './CameraScanner';

declare const config

const CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE: boolean = config.camera_scanner.enable_on_compatible_device
const CAMERA_SCANNER_READERS: (QuaggaJSReaderConfig | QuaggaJSCodeReader)[] = config.camera_scanner.readers

const CAMERA_PERMISSION_NOT_ALLOWED_ERRORS = ["NotAllowedError"]
const CAMERA_NOT_FOUND_ERRORS = ["NotFoundError"]

export const BarcodeSubscriber = defineComponent({
    mixins: [reactiveVueMixin],
    props: ['width', 'height', 'embedded', 'eventFilterIdentifier'],
    template: require('./barcode-subscriber.html'),
    data() {
        return {
            value: null as any,
            hasSeveralDevices: false,
            cameraReady: false,
            showWarning: false,
            warningMessage: '',

            // Icons
            lightBulbIcon: faLightbulb,
            barcodeIcon: faBarcode,
            cameraIcon: faCamera,
            changeIcon: faSyncAlt,
            loadingIcon: faCircleNotch,
            magicIcon: faMagic,

            cameraScanner: null as any as CameraScanner,
            torchAvailable: false,
        }
    },
    computed: {
        supportsCamera(): boolean {
            return !!(
                CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE &&
                navigator &&
                navigator.mediaDevices
            )
        },
    },
    created() {
        if(this.supportsCamera) {
            this.instanciateCameraScanner()
        }
    },
    async mounted() {
        if (this.supportsCamera) {
            console.info("BarCodeSubscriber: Supports camera")

            console.info("BarCodeSubscriber: Checking camera permission...")
            const cameraPermissionStatus: PermissionState | "UNKNOWN" = await this.getCameraPermissionStatus()
            switch(cameraPermissionStatus) {
                case 'denied':
                    console.info("BarCodeSubscriber: Checking camera permission -> denied")
                    this.warnUser(this.$t("alerts.permissions.camera.permissionDeniedWarning") as string)
                    return
                case 'prompt':
                    console.info("BarCodeSubscriber: Checking camera permission -> prompt")
                    this.warnUser(this.$t("alerts.permissions.camera.askForPermission") as string)
                    break
                case 'granted':
                    console.info("BarCodeSubscriber: Checking camera permission -> granted")
                    break
                case "UNKNOWN":
                    console.info("BarCodeSubscriber: Checking camera permission -> UNKNOWN")
                    // Logger
                    console.warn("Cannot get camera permission status")
                    break
            }

            // no need to call navigator.mediaDevices.getUserMedia, it is done by Quagga
            if(!this.cameraScanner) {
                this.instanciateCameraScanner()
            }
            console.info("BarCodeSubscriber: Starting scanner")
            this.startCameraScanner()

        } else {
            console.info("BarCodeSubscriber: Doesn't support camera")
            console.info("Camera scanner is enabled: ", CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE)
            this.subscribeToKeyPress();
        }
    },
    async beforeUnmount() {
        this.unsubscribeFromKeyPress();
        this.cameraScanner.resetCallbacks()
        // if we don't stop it manually, camera is still in use in the background
        console.info("BarCodeSubscriber: Stopping camera...")
        await this.cameraScanner.stop()
    },
    methods: {
        async getCameraPermissionStatus(): Promise<PermissionState | "UNKNOWN"> {
            try {
                // @ts-ignore
                const cameraPermissionStatus: PermissionStatus = await navigator.permissions.query({ name: 'camera' })

                if(cameraPermissionStatus?.state) {
                    return cameraPermissionStatus.state
                }
                return "UNKNOWN"
            } catch(error) {
                console.info("BarCodeSubscriber: Error while checking camera permission: ", error)
                // Logger
                return "UNKNOWN"
            }
        },
        instanciateCameraScanner() {
            console.info("BarCodeSubscriber: Instanciating camera scanner...")
            this.cameraScanner = new CameraScanner(
                CAMERA_SCANNER_READERS,
                () => {
                    console.info("BarCodeSubscriber: Camera ready")
                    this.cameraReady = true;
                },
                () => {
                    console.info("BarCodeSubscriber: Camera not ready")
                    this.cameraReady = false;
                },
                this.width,
                this.height,
                '#interactive',
            )
        },
        async startCameraScanner() {
            this.cameraScanner.pipe(first()).subscribe(async code => {
                code.split('').forEach(char => input$.next(
                    {
                        char,
                        inputEventIdentifier: this.eventFilterIdentifier,
                    }
                ));
                await this.close();
            });
            console.info("BarCodeSubscriber: Starting camera scanner...")
            await this.cameraScanner.start();
            // Check if torch is available
            const videoStream = (this.$refs.video as HTMLVideoElement)?.srcObject;
            if (videoStream) {
                //console.info("BarCodeSubscriber: video stream: ", videoStream)
                // @ts-ignore
                const capabilities = videoStream.getTracks()[0].getCapabilities();
                this.torchAvailable = !!capabilities.torch;
            }
            // check if the button to change camera has to be displayed
            this.hasSeveralDevices = this.cameraScanner.hasSeveralDevices;
        },
        async close() {
            if (this.supportsCamera) {
                console.info("BarCodeSubscriber: Stopping camera...")
                await this.cameraScanner.stop();
                this.$emit('close');
            } else {
                this.unsubscribeFromKeyPress();
            }
        },
        toggleTorch() {
            this.cameraScanner.toggleTorch();
        },
        async restartCameraScanner() {
            await this.cameraScanner.stop()
            this.cameraScanner.start()
        },
        changeCamera() {
            this.cameraScanner.goToNextCameraDevice();
        },
        subscribeToKeyPress() {
            window.addEventListener('keypress', this.onKeyPress);
        },
        unsubscribeFromKeyPress() {
            window.removeEventListener('keypress', this.onKeyPress);
        },
        onKeyPress(event: KeyboardEvent) {
            event.preventDefault();
            input$.next({ char: String.fromCharCode(event.which), inputEventIdentifier: this.eventFilterIdentifier });
        },
        warnUser(message: string) {
            this.showWarning = true
            this.warningMessage = message
        },
    },
})
